<template>
  <div class="banner-container">
    <div class="banner-top">
      <div class="banner-top-actions">
        <div class="search">
          <el-input v-model="value" placeholder="请输入内容" class="search-input"></el-input>
          <div class="search-icon" @click="search"><i class="el-icon-search"></i></div>
        </div>
        <button type="button" class="vip-open-btn" @click="goRechargeVip">开通会员</button>
      </div>
      <UserInfo @openLogin="$emit('openLogin')" @openResetPass="$emit('openResetPass')" />
    </div>

    <div class="chat-messages-container" ref="chatMessagesContainer" v-if="curId || history.length > 0">
      <div class="chat-messages" ref="chatMessages">
        <div v-if="history.length === 0" class="no-messages">暂无消息</div>
        <template v-else>
          <MarkdownSelect :messages="history" @refresh="handleRefreshEvent" @copy="handleCopyEvent" />
          <template v-if="buildingMessage.enable">
            <MarkdownItem :message="buildingMessage" />
            <div>
              <i class="el-icon-loading"></i>
              <span>思考中...</span>
            </div>
          </template>
        </template>
      </div>
    </div>

    <DefaultBanner :isLogin="isLogin" @selectQuestion="handleSelectQuestion" v-if="!curId && history.length === 0" />

    <MessageInput
      ref="messageInput"
      :isLogin="isLogin"
      :loading="loading"
      @openLogin="$emit('openLogin')"
      @send="handleSendMessage"
    />

    <div class="banner-footer">
      <div class="statement">
        服务生成的所有内容均由人工智能模型生成，其生成内容的准确性和完整性无法保证，不代表我们的态度或观点
      </div>
      <IcpBeian />
    </div>
  </div>
</template>

<script>
import { sendChatMessage } from "@/utils/sse";
import { listConversationMsg, saveConversationMsg } from '@/api';
import { saveBannerChatState, loadBannerChatState, clearBannerChatState } from '@/utils/chatState';
import { EventBus } from "@/eventBus";
import { isSessionLoggedIn, getStoredUser } from "@/utils/userSession";
import MarkdownSelect from '@/components/MarkdownSelect.vue';
import MarkdownItem from '@/components/MarkdownItem.vue';
import UserInfo from '@/components/UserInfo.vue';
import MessageInput from '@/components/MessageInput.vue';
import DefaultBanner from '@/components/DefaultBanner.vue';
import IcpBeian from '@/components/IcpBeian.vue';

export default {
  name: "BannerComponent",
  components: {
    MarkdownItem,
    MarkdownSelect,
    UserInfo,
    MessageInput,
    DefaultBanner,
    IcpBeian,
  },
  props: {
    curId: {
      default: null,
    },
  },
  data() {
    return {
      value: "",
      history: [],
      isLoading: false,
      hasMoreMessages: true,
      loading: false,
      params: {
        pageNum: 1,
        pageSize: 10,
      },
      buildingMessage: {
        enable: false,
        role: 'assistant',
        content: '',
        deepSeek: '',
        deepSeekTime: null,
        citations: [],
      },
      defaultUser: {},
      defaultVips: {},
      defaultStatistic: {},
      isSendingMessage: false,
      sessionVersion: 0,
    };
  },
  watch: {
    curId(val, oldVal) {
      if (val && val !== oldVal) {
        // 流式生成中切换会话 ID 时保留本地消息，不从服务端覆盖
        if (this.loading || this.buildingMessage.enable) {
          return;
        }
        if (!this.isSendingMessage) {
          this.initHistory();
          this.getHistoryV2();
        } else {
          this.isSendingMessage = false;
        }
        this.$nextTick(() => {
          this.$refs.chatMessagesContainer?.addEventListener("scroll", this.handleScroll);
        });
      } else if (!val) {
        if (this.loading || this.buildingMessage.enable) {
          return;
        }
        this.initHistory();
        this.$nextTick(() => {
          this.$refs.chatMessagesContainer?.removeEventListener("scroll", this.handleScroll);
          this.appendVipExpiredTipIfExpired();
        });
      }
    },
  },
  computed: {
    isLogin() {
      void this.sessionVersion;
      return isSessionLoggedIn();
    },
    user() {
      void this.sessionVersion;
      return getStoredUser() || this.defaultUser;
    },
  },
  methods: {
    onUserSessionUpdated() {
      this.sessionVersion += 1;
    },
    persistChatState() {
      saveBannerChatState({
        curId: this.curId,
        history: this.history,
        loading: this.loading,
        buildingMessage: this.buildingMessage,
        isSendingMessage: this.isSendingMessage,
        hasMoreMessages: this.hasMoreMessages,
      });
    },

    tryRestoreChatState() {
      const state = loadBannerChatState();
      if (!state) return false;

      const hasLocalContent =
        (state.history && state.history.length > 0) ||
        state.loading ||
        (state.buildingMessage && state.buildingMessage.enable);
      if (!hasLocalContent) return false;

      if (state.curId && this.curId && state.curId !== this.curId) return false;

      if (state.curId && !this.curId) {
        this.$emit('update:curId', state.curId);
      }

      this.history = state.history || [];
      this.loading = !!state.loading;
      this.isSendingMessage = !!state.isSendingMessage;
      this.hasMoreMessages = state.hasMoreMessages ?? true;
      if (state.buildingMessage) {
        this.buildingMessage = {
          enable: !!state.buildingMessage.enable,
          role: state.buildingMessage.role || 'assistant',
          content: state.buildingMessage.content || '',
          deepSeek: state.buildingMessage.deepSeek || '',
          deepSeekTime: state.buildingMessage.deepSeekTime ?? null,
          citations: state.buildingMessage.citations || [],
        };
      }
      return true;
    },

    goRechargeVip() {
      this.persistChatState();
      this.$router.push({ name: 'recharge-vip' });
    },
    initHistory() {
      this.hasMoreMessages = true;
      this.params.pageNum = 1;
      this.history = [];
    },

    getHistoryV2() {
      if (this.isLoading || !this.hasMoreMessages || !this.isLogin) return;

      const isFirstPage = this.params.pageNum === 1;
      this.isLoading = true;
      listConversationMsg(this.user?.nickName, this.curId)
        .then(({ data }) => {
          const total = Object.keys(data).length;
          const rows = data.map((item) => ({
            conversationId: item.conversationId,
            role: item.role,
            content: item.content,
            deepSeek: item.deepSeek,
            deepSeekTime: item.deepSeekTime,
            citations: item.citations,
          }));

          this.isLoading = false;
          this.history = this.history.length ? [...rows, ...this.history] : [...rows];
          this.hasMoreMessages = this.history.length < total;

          if (this.params.pageNum === 1) {
            this.$nextTick(() => {
              this.scrollToBottom();
            });
          }

          if (this.hasMoreMessages) {
            this.params.pageNum++;
          }
        })
        .catch((error) => {
          this.isLoading = false;
          console.error("加载历史消息失败:", error);
          this.$message.error("加载历史消息失败，请稍后重试");
          if (isFirstPage) {
            this.$nextTick(() => {
              this.scrollToBottom();
            });
          }
        });
    },

    isVipExpired() {
      if (!this.isLogin) return false;
      try {
        const userinfo = JSON.parse(localStorage.getItem('userinfo') || 'null');
        if (userinfo != null && userinfo.dayTime !== undefined && userinfo.dayTime !== null) {
          if (Number(userinfo.dayTime) < 0) return true;
        }
        const vips = JSON.parse(localStorage.getItem('vips') || 'null');
        if (vips != null && vips.remainDays !== undefined && vips.remainDays !== null) {
          if (Number(vips.remainDays) < 0) return true;
        }
      } catch (e) {
        /* ignore */
      }
      return false;
    },

    buildVipExpiredAssistantMessage() {
      return {
        id: `vip-expired-recharge-${Date.now()}`,
        role: 'assistant',
        msgStatus: -1,
        vipExpiredRechargeTip: true,
        content: '会员服务已过期，可充值恢复权益',
      };
    },

    appendVipExpiredTipIfExpired() {
      if (!this.isVipExpired()) return;
      const last = this.history[this.history.length - 1];
      if (last && last.vipExpiredRechargeTip) return;
      this.history.push(this.buildVipExpiredAssistantMessage());
    },

    search() {
      if (this.loading) return this.$message.warning('AI回答中，请稍后...');
      if (!this.$refs.messageInput?.getSelectedTags()) {
        return this.$message.warning('请选择类型');
      }
      this.initHistory();
      this.$emit('addConversation', null);
      this.$emit('update.curId', null);
      this.inputMessage = this.value;
      this.$nextTick(() => {
        this.$refs.messageInput?.setInputMessage(this.value);
        this.value = '';
      });
    },

    searchAIV2(tags, pendingUserMessage) {
      if (this.isVipExpired()) {
        this.resetBuildingMessage();
        this.loading = false;
        this.isSendingMessage = false;
        return;
      }

      const history = this.history?.map((item) => ({ role: item.role, content: item.content }));

      const params = {
        'model': this.$refs.messageInput.questionParams.modelKey  || 'doubao',
        'topic_ids': [this.$refs.messageInput.selectedTags] ||[tags],
        'messages': history,
        'stream': true,
        'temperature': this.$refs.messageInput.questionParams.temperature || 0.7,
        'maxTokens': this.$refs.messageInput.questionParams.max_token || 5120,
        'extensions': {
          'enableReasoning': this.$refs.messageInput.questionParams.deep_seek || true,
          'enableSearch': this.$refs.messageInput.questionParams.enable_search || true,
        },
      };

      this.buildingMessage.enable = true;
      this.loading = true;
      const thinkingStart = Date.now();

      sendChatMessage(params, (lineObj) => {
        let choices = lineObj?.choices;
        let usage = lineObj?.usage;                 // 使用情况
        let inputTokens = usage?.inputTokens || 0;  // 输入消耗tokens
        let outputTokens = usage?.outputTokens || 0;// 输出消耗tokens
        let citations = lineObj?.citations;
        let content = "";
        let thinkingContent = "";
        let isStop = false;

        if (choices != null && choices.length > 0) {
          content = choices[0].delta?.content;
          thinkingContent = choices[0]?.delta?.reasoningContent;
          isStop = choices[0]?.finishReason != null;
        }

        if (!!content) {
          this.buildingMessage.content += content;
        }
        if (!!thinkingContent) {
          this.buildingMessage.deepSeek += thinkingContent;
          this.buildingMessage.deepSeekTime = (Date.now() - thinkingStart) / 1000;
        }
        if (citations != null && citations.length > 0) {
          this.buildingMessage.citations = citations;
        }

        const newConversationId = this.curId ?? `c${new Date().getTime()}`;

        if (isStop) {
          const assistantMessage = {
            'id': `m${new Date().getTime()}`,
            'conversationId': this.curId,
            'role': 'assistant',
            'msgStatus': 200,
            'content': this.buildingMessage.content || '没有找到答案。',
            'deepSeek': this.buildingMessage.deepSeek,
            'deepSeekTime': this.buildingMessage.deepSeekTime,
            'citations': this.buildingMessage.citations,
            'tokens': inputTokens,
          };
          pendingUserMessage.tokens = outputTokens;

          // 第一步：先保存用户消息
          saveConversationMsg({
            'user_name': this.user.nickName,
            'conversation_id': newConversationId,
            'conversation_data': pendingUserMessage,
          })
            .then(() => {
              // 第二步：用户消息保存完成，再保存AI消息
              return saveConversationMsg({
                'user_name': this.user.nickName,
                'conversation_id': newConversationId,
                'conversation_data': assistantMessage,
              });
            })
            .then(() => {
              // 两步都完成后，执行最终逻辑
              this.history.push(assistantMessage);
              this.resetBuildingMessage();
              this.loading = false;
              clearBannerChatState();
              // 刷新左侧会话列表
              this.$emit('refreshConversation');
            });

          this.$nextTick(() => this.scrollToBottom());
        }
      });
    },

    handleSendMessage({ message, tags }) {
      if (!this.isLogin) {
        this.$emit("openLogin");
        return;
      }

      this.scrollToBottom();
      this.history.push({
        role: 'user',
        content: message,
      });

      if (this.isVipExpired()) {
        this.history.push(this.buildVipExpiredAssistantMessage());
        this.loading = false;
        this.isSendingMessage = false;
        this.$nextTick(() => this.scrollToBottom());
        return;
      }

      this.loading = true;
      this.isSendingMessage = true;

      const newConversationId = this.curId ?? `c${new Date().getTime()}`;

      // 构建用户消息对象，但不立即保存，等待AI回复完成后一起保存
      const pendingUserMessage = {
        'id': `m${new Date().getTime()}`,
        'conversationId': newConversationId,
        'role': 'user',
        'msgStatus': 200,
        'content': message,
        'deepSeek': null,
        'deepSeekTime': null,
        'citations': [],
      };

      // 立即触发会话创建和AI请求，不等待保存接口
      this.$emit('addConversation', newConversationId);
      this.$emit('update:curId', newConversationId);
      this.hasMoreMessages = false;
      this.searchAIV2(tags, pendingUserMessage);
    },

    handleSelectQuestion(question) {
      this.$refs.messageInput?.setInputMessage(question);
      this.$refs.messageInput?.sendUserMessage();
    },

    scrollToBottom() {
      const chatMessagesContainer = this.$refs.chatMessagesContainer;
      if (chatMessagesContainer) {
        chatMessagesContainer.scrollTop = chatMessagesContainer.scrollHeight;
      }
    },

    handleScroll() {
      const chatMessagesContainer = this.$refs.chatMessagesContainer;
      if (!chatMessagesContainer) return;

      if (
        chatMessagesContainer.scrollTop <= 30 &&
        !this.isLoading &&
        this.hasMoreMessages
      ) {
        this.getHistoryV2();
      }
    },

    copy(text) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      this.$message.success('复制成功！');
    },

    reAnswer() {
      if (this.isVipExpired()) {
        this.history.push(this.buildVipExpiredAssistantMessage());
        this.$nextTick(() => this.scrollToBottom());
        return;
      }
      const latestUserMsg = this.history[this.history.length - 2];
      this.history.push(latestUserMsg);
      this.$refs.messageInput?.setInputMessage(latestUserMsg.content);
      this.loading = true;

      // 构建用户消息对象（从历史记录中获取）
      const pendingUserMessage = {
        'id': `m${new Date().getTime()}`,
        'conversationId': this.curId ?? `c${new Date().getTime()}`,
        'role': 'user',
        'msgStatus': 200,
        'content': latestUserMsg.content,
        'deepSeek': null,
        'deepSeekTime': null,
        'citations': [],
      };

      this.searchAIV2(this.$refs.messageInput?.getSelectedTags(), pendingUserMessage);
    },

    resetBuildingMessage() {
      this.buildingMessage = {
        enable: false,
        content: '',
        deepSeek: '',
        deepSeekTime: null,
        citations: [],
      };
    },

    handleRefreshEvent(message, index) {
      this.reAnswer();
    },

    handleCopyEvent(message, index) {
      this.copy(message.content);
    },
  },
  mounted() {
    if (!this.isLogin) return;

    if (this.history.length === 0 && !this.loading && !this.buildingMessage.enable) {
      const restored = this.tryRestoreChatState();
      if (!restored && this.curId) {
        this.getHistoryV2();
      }
    }

    this.$nextTick(() => {
      if (!this.curId) {
        this.appendVipExpiredTipIfExpired();
      }
      if (this.history.length || this.buildingMessage.enable) {
        this.scrollToBottom();
      }
    });
  },
  activated() {
    // 实例仍在内存中时不覆盖（SSE 可能在后台继续更新了 buildingMessage）
    if (this.history.length === 0 && !this.loading && !this.buildingMessage.enable) {
      this.tryRestoreChatState();
    }
    if (this.history.length || this.buildingMessage.enable) {
      this.$nextTick(() => this.scrollToBottom());
    }
  },
  deactivated() {
    this.persistChatState();
  },
  beforeDestroy() {
    EventBus.$off('persist-chat-state', this._persistChatState);
    EventBus.$off('user-session-updated', this._onUserSessionUpdated);
    if (this.$refs.chatMessagesContainer) {
      this.$refs.chatMessagesContainer?.removeEventListener("scroll", this.handleScroll);
    }
  },
  created() {
    this._persistChatState = () => this.persistChatState();
    this._onUserSessionUpdated = () => this.onUserSessionUpdated();
    EventBus.$on('persist-chat-state', this._persistChatState);
    EventBus.$on('user-session-updated', this._onUserSessionUpdated);
  },
};
</script>

<style scoped lang="scss">
.banner-container {
  height: 100%;
  position: relative;
  background-color: #fff;
  border-radius: 30px;
  overflow: hidden;
  background-image: url("../assets/bg.png");
  background-position: bottom;
  background-repeat: no-repeat;
  padding-top: 30px;
}

.chat-messages-container {
  width: 80%;
  height: calc(100% - 300px);
  margin: 0 auto;
  overflow-y: auto;

  .no-messages {
    text-align: center;
    color: #999;
    padding: 20px;
  }
}

.banner-top {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  padding-right: 25px;
  margin-bottom: 40px;

  .banner-top-actions {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .vip-open-btn {
    height: 54px;
    padding: 0 24px;
    border: none;
    border-radius: 12px;
    color: #fff;
    font-size: 16px;
    font-weight: 600;
    font-family: inherit;
    letter-spacing: 0.5px;
    cursor: pointer;
    white-space: nowrap;
    background: linear-gradient(309deg, #ffc258 0%, #f4a113 100%);
    box-shadow: 0 4px 14px rgba(244, 161, 19, 0.38);
    transition: filter 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;
  }

  .vip-open-btn:hover {
    filter: brightness(1.06);
    box-shadow: 0 6px 20px rgba(244, 161, 19, 0.48);
  }

  .vip-open-btn:active {
    transform: scale(0.98);
  }

  .search {
    width: 346px;
    height: 54px;
    background: #ffffff;
    border-radius: 8px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    &-input {
      flex: 1;
    }

    &-icon {
      width: 40px;
      height: 40px;
      background: linear-gradient(309deg, #ffc258 0%, #f4a113 100%);
      border-radius: 7px;
      display: flex;
      justify-content: center;
      align-items: center;
      color: #fff;
      font-size: 21px;
      cursor: pointer;
    }
  }
}

.banner-footer {
  position: absolute;
  bottom: 12px;
  left: 0;
  width: 100%;
  padding: 0 16px;
  box-sizing: border-box;
}

.statement {
  text-align: center;
  width: 100%;
  margin-bottom: 6px;
  font-size: 14px;
  color: #bcbcbc;
  line-height: 20px;
}
</style>

<style scoped lang="scss">
::v-deep .message-content {
  table {
    width: 100%;
    font-size: 14px;
    border-collapse: collapse;
    border-spacing: 0;
    border: 1px solid #ddd;

    th {
      text-align: left;
      background-color: #E8EBF8;
      padding: 10px;
      border: 1px solid #ccc;
    }

    td {
      text-align: left;
      padding: 10px;
      border: 1px solid #ccc;
    }
  }

  pre {
    background-color: #1E1E1E;
    padding: 10px;
    border-radius: 8px;
    color: #abb2bf;
    position: relative;

    code {
      font-family: ui-monospace, "JetBrains Mono", SFMono-Regular, SF Mono, Menlo, Consolas, Liberation Mono, monospace, "JetBrains Mono";
    }

    .copy-button {
      position: absolute;
      top: 10px;
      right: 10px;
      background-color: #9999AA;
      color: white;
      border: none;
      padding: 5px 10px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 12px;
      z-index: 10;

      &:hover {
        background-color: #444444;
      }
    }
  }
}
</style>
